/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { memo, useCallback } from 'react';
import { useIsStreamingEnabled } from '@jira/ssr__streaming-enabled/src/useIsStreamingEnabled.tsx';
import {
	PAGE_LAYOUT_MARK,
	PAGE_CONTAINER_END_MARK,
	GLOBAL_CONTAINERS_END_MARK,
} from '@jira/platform__page-container-constants/src/index.tsx';
import { ForgeUiGlobalExtensionsAsync } from '@jira/forge__forge-ui-global-extensions/src/ui/async.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import PerformanceMark from '@atlassian/jira-common-performance/src/set-performance-mark.tsx';
import { setInitialPageLoadTimingFromPerformanceMarks } from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFaviconService } from '@atlassian/jira-favicon/src/index.tsx';
import { PageContainer } from '../../common/ui/page-container/main.tsx';
import { Content } from './content/index.tsx';
import type { SpaProps } from './types.tsx';
import { ReadinessTracker } from './readiness-tracker.tsx';
import { HydrationAdvisory } from './HydrationAdvisory.tsx';
import { StreamingAdvisory } from './StreamingAdvisory.tsx';

const FaviconService = memo(() => {
	useFaviconService();

	return null;
});

export const Spa = ({
	locale,
	messages,
	Router,
	routes,
	routerLinkComponent,
	initRoute,
}: SpaProps): React.JSX.Element => {
	const isStreamingEnabled = useIsStreamingEnabled();

	const pageContainerPerformanceTiming = useCallback(() => {
		setInitialPageLoadTimingFromPerformanceMarks(
			PAGE_LAYOUT_MARK,
			GLOBAL_CONTAINERS_END_MARK,
			PAGE_CONTAINER_END_MARK,
			true,
			true,
		);
	}, []);

	const content = (
		<PageContainer
			id={__SERVER__ ? 'jira.entry.ssr' : 'spa'}
			locale={locale}
			messages={messages}
			Router={Router}
			routerLinkComponent={routerLinkComponent}
		>
			<ReadinessTracker />
			<FaviconService />
			<PerformanceMark metricKey={PAGE_CONTAINER_END_MARK} onSet={pageContainerPerformanceTiming} />
			<Content initRoute={initRoute} routes={routes} />

			{fg('ditto_fcf_jira_globalbackgroundscript') && <ForgeUiGlobalExtensionsAsync />}
		</PageContainer>
	);

	if (isStreamingEnabled) {
		return (
			<>
				<Placeholder name="root">{content}</Placeholder>
				{/* Rendered outside page container to avoid adding any DOM nodes to layout */}
				<StreamingAdvisory />
			</>
		);
	}

	if (fg('jfp-magma-hydration-advisory')) {
		return (
			<>
				{content}
				{/* Rendered outside page container to avoid adding any DOM nodes to layout */}
				<HydrationAdvisory />
			</>
		);
	}
	return content;
};
